/**
 * PAPPO — cropImage
 * Helper de canvas para recortar uma imagem num quadrado a partir da área
 * (em pixels) devolvida pelo react-easy-crop. Devolve um File JPEG pronto
 * para upload (avatares / foto de grupo).
 */

import type { Area } from 'react-easy-crop';

/** Carrega um HTMLImageElement a partir de uma URL (object URL). */
function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (err) => reject(err));
    image.src = url;
  });
}

/**
 * Recorta `imageSrc` na região `pixelCrop` (quadrada) e reduz para no máximo
 * `maxSize`px de lado — o suficiente para um avatar nítido sem ficheiros gigantes.
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  fileName = 'avatar.jpg',
  maxSize = 512,
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não foi possível processar a imagem.');

  const size = Math.min(maxSize, Math.round(pixelCrop.width) || maxSize);
  canvas.width = size;
  canvas.height = size;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    size,
    size,
  );

  return new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Falha ao gerar a imagem.'));
          return;
        }
        resolve(new File([blob], fileName, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.9,
    );
  });
}
